import { filter } from 'lodash';
import { FormQuestionTypeKeys } from 'ui-model';

import { ProvisioningEnum, RolloutEnum } from '@lib/resources/types';

import usePermissions from 'hooks/usePermissions';
import { PATIENT_FORM_VERSION_REDESIGN_POSTFIX } from 'screens/MissionControl/Utilities/NewFormBuilderPage/constants';
import { useHasAccessToJumpLogicV2 } from 'screens/MissionControl/Utilities/NewFormBuilderPage/hooks';
import { FormTypeKeys } from 'screens/MissionControl/Utilities/NewFormBuilderPage/utils';
import { useGetVendorSecretByName } from 'screens/MissionControl/Utilities/ThirdPartyIntegrations/hooks/useVendorSecretModel';

export default function useFilterAllowedQuestionType<
  T extends { key: string; isCCDA?: boolean | string; [key: string]: unknown }
>({ formType, formVersion = '', questions }: { formType?: FormTypeKeys; formVersion?: string; questions: T[] }) {
  const { hasAccessToProduct } = usePermissions();
  const hasAccessToSchedulerQuestion = hasAccessToProduct('scheduler-question' as RolloutEnum);
  const canUseJumpLogic = useHasAccessToJumpLogicV2(formVersion);
  const isSchedulerAssistFormType = formType === FormTypeKeys.schedulerAssist;
  const hasSchedulerAssistAccess = hasAccessToProduct(RolloutEnum.SchedulerAssist);
  const hasAccessToResourcesQuestion = hasSchedulerAssistAccess && isSchedulerAssistFormType;
  const isFormRedesign = formVersion?.includes(PATIENT_FORM_VERSION_REDESIGN_POSTFIX);
  const { data: vendorSecretData = { response: [] }, isLoading } = useGetVendorSecretByName({ name: 'docusign' });

  if (!formType && !formVersion) return questions;

  const isNextGentEnv = /next\.nextgen\.lumahealthdemo\.com/.test(window.location.origin);
  const hasAccessToIMH = hasAccessToProduct('imh-questionnaire' as RolloutEnum) || isNextGentEnv;

  const vendorSecrets = vendorSecretData.response[0];

  let items = [...questions];

  // insurance-verification provisioning allow access to luma pay stuff
  if (!hasAccessToProduct([ProvisioningEnum.LumaPay, ProvisioningEnum.InsuranceVerification])) {
    items = filter(items, (item) => item._id !== FormQuestionTypeKeys.copay);
  }

  // handle Jump-logicV2 versioning
  if (canUseJumpLogic) {
    items = filter(items, (item) => item._id !== FormQuestionTypeKeys.jump);
  }

  if (!hasAccessToResourcesQuestion) {
    items = filter(items, (item) => item._id !== 'resources');
  }

  // display docusign question only if in case there is already a docusign account integrated
  if (!isLoading && !vendorSecrets) {
    items = filter(items, (item) => item._id !== 'docusign_templates');
  }

  // display scheduler question only for pre-scheduling forms
  // TODO remove this rollout verification
  // @ts-ignore
  if (formType !== FormTypeKeys.preQualification || !hasAccessToSchedulerQuestion) {
    items = filter(items, (item) => item._id !== 'scheduler');
  }

  // display two factor question only for pre-scheduling forms
  if (formType !== FormTypeKeys.preQualification) {
    items = filter(items, (item) => item._id !== FormQuestionTypeKeys.twoFactor);
  }

  // display insurance card question only if in case there is already a insurance card account integrated;
  const hasAccessToLegacyInsuranceCard = hasAccessToProduct('legacy-insurance-card-question' as RolloutEnum);
  if (!hasAccessToLegacyInsuranceCard) {
    items = filter(items, (item) => item._id !== FormQuestionTypeKeys.insuranceCard);
  }

  if (!hasAccessToIMH) {
    items = filter(items, (it) => it._id !== 'imh');
  }

  if (isSchedulerAssistFormType) {
    items = filter(
      items,
      (item) =>
        item._id !== FormQuestionTypeKeys.informationCapture &&
        item._id !== FormQuestionTypeKeys.insuranceCard &&
        item._id !== FormQuestionTypeKeys.copay &&
        item._id !== 'docusign_templates' &&
        item._id !== 'imh' &&
        item._id !== FormQuestionTypeKeys.eSignature,
    );
  }

  if (isFormRedesign) {
    // Avoid non-supported questions by the new forms
    items = filter(
      items,
      (item) =>
        item._id !== FormQuestionTypeKeys.informationCapture &&
        item._id !== FormQuestionTypeKeys.insuranceCard &&
        item._id !== 'resources' &&
        item._id !== 'scheduler' &&
        item._id !== FormQuestionTypeKeys.yesNo,
    );
  }

  const isCCDA = formType === FormTypeKeys.ccda;

  const itemsToReturn = isCCDA ? [...items] : items.filter((it) => !it.isCCDA);

  return itemsToReturn;
}
